import { useState } from 'react';
import { AIAssistantStatus } from '@/types';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, XCircle, TestTube2 } from 'lucide-react';
import { StatusConfirmationDialog } from './StatusConfirmationDialog';
import { useUpdateMyIABlueAssistantStatus } from '@/hooks/useMyIABlueAssistant';
import { useToast } from '@/hooks/use-toast';

interface AssistantStatusToggleProps {
  currentStatus: AIAssistantStatus;
}

const STATUS_CONFIG = {
  [AIAssistantStatus.ACTIVE]: {
    label: 'Ativo',
    icon: CheckCircle2,
    badgeClass: 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200',
  },
  [AIAssistantStatus.INACTIVE]: {
    label: 'Inativo',
    icon: XCircle,
    badgeClass: 'bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-200',
  },
  [AIAssistantStatus.TEST]: {
    label: 'Teste',
    icon: TestTube2,
    badgeClass: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200',
  },
};

export function AssistantStatusToggle({ currentStatus }: AssistantStatusToggleProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<AIAssistantStatus | null>(null);
  const { toast } = useToast();
  const updateStatusMutation = useUpdateMyIABlueAssistantStatus();

  const config = STATUS_CONFIG[currentStatus];
  const Icon = config.icon;

  const handleStatusChange = (newStatus: string) => {
    if (newStatus === currentStatus) return;

    setPendingStatus(newStatus as AIAssistantStatus);
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!pendingStatus) return;

    try {
      await updateStatusMutation.mutateAsync(pendingStatus);
      toast({
        title: 'Status atualizado',
        description: `O assistente está agora ${STATUS_CONFIG[pendingStatus].label.toLowerCase()}.`,
      });
      setPendingStatus(null);
    } catch (error) {
      toast({
        title: 'Erro ao atualizar status',
        description: 'Não foi possível atualizar o status do assistente. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setPendingStatus(null);
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 border rounded-lg bg-card">
        <div className="flex items-center gap-2 flex-1">
          <Badge variant="outline" className={`${config.badgeClass} flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium`}>
            <Icon className="h-4 w-4" />
            {config.label}
          </Badge>
          <span className="text-sm text-muted-foreground hidden sm:inline">
            Status atual do assistente
          </span>
        </div>

        <Select value={currentStatus} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Alterar status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AIAssistantStatus.ACTIVE}>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>Ativar</span>
              </div>
            </SelectItem>
            <SelectItem value={AIAssistantStatus.INACTIVE}>
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <span>Desativar</span>
              </div>
            </SelectItem>
            <SelectItem value={AIAssistantStatus.TEST}>
              <div className="flex items-center gap-2">
                <TestTube2 className="h-4 w-4 text-yellow-600" />
                <span>Modo Teste</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {pendingStatus && (
        <StatusConfirmationDialog
          open={dialogOpen}
          onOpenChange={handleCancel}
          currentStatus={currentStatus}
          newStatus={pendingStatus}
          onConfirm={handleConfirm}
          isLoading={updateStatusMutation.isPending}
        />
      )}
    </>
  );
}
